import { ReCaptchaStatusEnum, SnackBarVariants } from '@app/app.enum';
import { history } from '@app/app.routes';
import * as maerskLogo from '@app/assets/images/maersk-logo-rgb.svg';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import PasswordTextFieldComponent from '@app/components/shared/password-text-field.component';
import { ChangePasswordRegex } from '@app/constants';
import environment from '@app/environments/environment';
import * as models from '@app/models/index';
import { Module } from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { Button, Card, CardContent, CircularProgress, Typography } from '@material-ui/core';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import * as queryString from 'query-string';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { injectIntl } from 'react-intl';
import { Redirect } from 'react-router';
import * as Yup from 'yup';

const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);
const sharedServive: SharedService = Injector.get(SharedService);
interface IResetPasswordFormFields {
	password: string;
	confirmPassword: string;
	reCaptchaResponse: string;
}
const ResetPasswordSchema = Yup.object({
	confirmPassword: Yup.string()
		.oneOf(
			[Yup.ref('password')],
			intlService.GetFormattedMessage('app.reset.password.reset-password-form.password-confirm-password-same-valiation')
		)
		.required(
			intlService.GetFormattedMessage('app.reset.password.reset-password-form.confirm-password-required-validation')
		),
	password: Yup.string()
		.required(intlService.GetFormattedMessage('app.signup.signup-form.password-required-validation'))
		.matches(ChangePasswordRegex, intlService.GetFormattedMessage('app.profile.change-password-form.regex-validation')),
	reCaptchaResponse: Yup.string().notRequired(),
});
class ResetPasswordComponent extends React.Component<models.IResetPasswordProps, models.IResetPasswordState> {
	private recaptchaRef = React.createRef<ReCAPTCHA>();
	constructor(props: models.IResetPasswordProps) {
		super(props);
		this.state = models.initialResetPassword();
		this.bindFunctions();
	}
	private get FormFactory(): IResetPasswordFormFields {
		return {
			confirmPassword: '',
			password: '',
			reCaptchaResponse: '',
		};
	}

	// if getting query param for reset password then trigger component else redirect to sign in
	public componentDidMount() {
		if (this.props.location.search) {
			this.getQueryParams();
		}
	}

	public render() {
		if (this.props.location.search) {
			return (
				<main className='reset-password-root'>
					<div className='text-center pb-2'>
						<div className='genesis-text'>
							<img src={maerskLogo} className='genesis-logo' />
						</div>
					</div>
					<Card>
						<CardContent>
							<div className='text-center'>
								<Typography component='h3' variant='h6'>
									{this.props.intl.formatMessage({
										id: 'app.reset.password-title',
									})}
								</Typography>
							</div>
							<Formik
								initialValues={this.FormFactory}
								enableReinitialize={true}
								onSubmit={(e: IResetPasswordFormFields) => {
									this.onResetPassword(e);
								}}
								validationSchema={ResetPasswordSchema}
								render={(props: FormikProps<FormikValues>) => (
									<Form noValidate={true}>
										<Field
											label='Password'
											margin='normal'
											variant='outlined'
											component={PasswordTextFieldComponent}
											type='password'
											required={true}
											fullWidth={true}
											onChange={(e: React.ChangeEvent) => props.handleChange('password')(e)}
											error={!!((props.values.password.length || props.submitCount) && !!props.errors.password)}
											helperText={props.values.password.length || props.submitCount ? props.errors.password : null}
										/>
										<Field
											label='Confirm Password'
											margin='normal'
											variant='outlined'
											component={PasswordTextFieldComponent}
											type='password'
											required={true}
											fullWidth={true}
											onChange={(e: React.ChangeEvent) => props.handleChange('confirmPassword')(e)}
											error={
												!!((props.values.confirmPassword.length || props.submitCount) && !!props.errors.confirmPassword)
											}
											helperText={
												props.values.confirmPassword.length || props.submitCount ? props.errors.confirmPassword : null
											}
										/>

										{environment.reCaptchaStatus !== ReCaptchaStatusEnum.Disabled && (
											<React.Fragment>
												<div className='pt-1' />
												<ReCAPTCHA
													sitekey={environment.googleCaptchaKey}
													badge='bottomleft'
													onChange={(token: string) => {
														props.setFieldValue('reCaptchaResponse', token);
													}}
													ref={this.recaptchaRef as React.LegacyRef<ReCAPTCHA> & React.LegacyRef<HTMLDivElement>}
												/>
											</React.Fragment>
										)}
										<Button
											className='mt-1 mb-1'
											type='submit'
											fullWidth={true}
											color='primary'
											disabled={
												this.state.loading ||
												(environment.reCaptchaStatus === ReCaptchaStatusEnum.Enabled
													? !props.values.reCaptchaResponse
													: false)
											}
										>
											{this.props.intl.formatMessage({
												id: 'app.reset.password.reset-password-form.reset-password-label',
											})}
											{this.state.loading && <CircularProgress size={24} className='ml-1' />}
										</Button>
									</Form>
								)}
							/>
						</CardContent>
					</Card>
				</main>
			);
		} else {
			return this.redirectToSignIn();
		}
	}

	// open  snackbar
	public openSnackbar = (message: string | string[], variantName: SnackBarVariants) => {
		this.setState({
			loading: false,
		});
		this.props.openSnackBar(message, variantName);
	};

	private async onResetPassword(values: IResetPasswordFormFields) {
		try {
			this.setState({ loading: true });
			if (!this.state.email || !this.state.code) {
				this.openSnackbar(
					this.props.intl.formatMessage({
						id: 'app.snackbar.reset-password-email-wrong',
					}),
					SnackBarVariants.Error
				);
			} else {
				await accountService.userResetPassword(
					{
						code: this.state.code,
						email: this.state.email,
						password: values.password,
						confirmPassword: values.confirmPassword,
						reCaptchaResponse: values.reCaptchaResponse,
					},
					this.props.axiosCancellationToken
				);

				this.onResetPasswordSuccess();
			}
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		} finally {
			this.recaptchaRef.current?.reset();
			values.reCaptchaResponse = null;
		}
	}

	// get security code and email from query params
	private getQueryParams() {
		const queryParams = queryString.parse(this.props.location.search);
		if (queryParams.code.toString() && queryParams.email.toString()) {
			this.setState({
				code: queryParams.code.toString(),
				email: queryParams.email.toString(),
			});
		}
	}

	private redirectToSignIn() {
		return <Redirect to='/sign-in' />;
	}

	private onResetPasswordSuccess() {
		this.openSnackbar(
			this.props.intl.formatMessage({
				id: 'app.snackbar.reset-password-successfully',
			}),
			SnackBarVariants.Success
		);
		setTimeout(() => {
			history.push(Module.Dashboard.url);
		}, 2000);
	}

	private bindFunctions() {}
}
export default withAxiosCancellation(withSnackBar(injectIntl(ResetPasswordComponent)));



// WEBPACK FOOTER //
// ./src/components/account/reset-password/reset-password.component.tsx